"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  paginate,
  type components,
  type Group,
  type Page,
  type paths,
} from "@theta/api-client";
import { thetaApi, dataOf, pageOf } from "@/lib/api/theta";
import { invalidateGroupDocuments } from "@/lib/hooks/useGroupPage";
import { retryUnlessRefused } from "./queryRetry";

/**
 * THE GROUPS COLLECTION, ON /api/v1 — spec §2.14
 *
 * `lib/api/groups.ts` is GONE and this is what replaced it. What that module
 * did that nothing here does any more:
 *
 *  - `getGroupBySlug` + `getGroup`. TWO reads of one thing, because a slug was
 *    not an address. `{ref}` is either (D8), so `useV1Group` in
 *    `useGroupPage.ts` is the only group read there is and every screen that
 *    used to resolve a slug to an id now just passes the slug down.
 *  - `getGroups` / `getMyGroups` / `getSubGroups` / `getGroupLocations`. FOUR
 *    routes; `?sort=`, `?member=me`, `?parent=` and `?hasLocation=` on one
 *    collection. `useV1Groups` is all four.
 *  - `setGroupActivityPub` and the separate moderation-settings PUT: three
 *    legacy verbs merged into one PATCH, so `useUpdateGroup` is one mutation.
 *  - `toggleFavouriteGroup`, a PUT with no body whose answer depended on what
 *    the row happened to be. Two tabs toggling twice left it in whichever
 *    state the race decided; `PATCH …/membership {isFavourite}` states the
 *    value it wants.
 *
 * `?page=` is gone with them. Every listing here is cursor-paged (§1.3) and
 * NOTHING carries a `total` — a screen that wants to say "N grupper" needs a
 * facet or a different sentence, not a second request (D4).
 */

type Schemas = components["schemas"];

/** One row of the map's facet: a place, and how many groups are at it. */
export type GroupLocation = Schemas["GroupLocation"];
export type GroupStats = Schemas["GroupStats"];
export type GroupAttachment = Schemas["GroupAttachment"];
/** Named from the document, so a sort the server drops stops compiling here. */
export type GroupListSort = NonNullable<
  NonNullable<paths["/groups"]["get"]["parameters"]["query"]>["sort"]
>;

/**
 * A 404 IS A REAL ANSWER (D7) and every read in this domain can give one: a
 * secret group, a group that does not exist, an admin-only read reached by a
 * member — all three are the same bytes, deliberately. Retrying it is retrying
 * a correct response, so no read here does.
 */
interface GroupListFilters {
  q?: string | undefined;
  sort?: GroupListSort | undefined;
  /** A group id or slug, or `"none"` for top-level groups only. */
  parent?: string | number | undefined;
  /** Only the groups the viewer is an ACTIVE member of. */
  memberOnly?: boolean | undefined;
  /** `kommune:0101` — the drill-down half of the map's facet. */
  location?: string | undefined;
  limit?: number | undefined;
}

/**
 * The key carries EVERY filter, and `limit` with them.
 *
 * Not tidiness: §1.3 folds the filter set into the cursor, so a cursor minted
 * under one set is a `400 cursorMismatch` under another — and two screens
 * sharing a key while asking for different pages is how a cache serves one of
 * them the other's rows.
 */
function groupsKey(filters: GroupListFilters): (string | number | boolean)[] {
  return [
    "v1",
    "groups",
    filters.q ?? "",
    filters.sort ?? "",
    filters.parent === undefined ? "" : String(filters.parent),
    filters.memberOnly ?? false,
    filters.location ?? "",
    filters.limit ?? 0,
  ];
}

/**
 * One page of the groups collection.
 *
 * `hasMore`/`nextCursor` and nothing else: the old `meta.page` counter is the
 * exact bug D4 names — a list that silently stops at page one because the
 * caller read an offset that is no longer there.
 */
export function useV1Groups(filters: GroupListFilters = {}, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: groupsKey(filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      thetaApi.http
        .GET("/groups", {
          params: {
            query: {
              ...(filters.q ? { q: filters.q } : {}),
              ...(filters.sort ? { sort: filters.sort } : {}),
              ...(filters.parent !== undefined ? { parent: String(filters.parent) } : {}),
              ...(filters.memberOnly ? { member: "me" as const } : {}),
              ...(filters.location ? { location: filters.location } : {}),
              ...(filters.limit ? { limit: filters.limit } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        })
        .then(pageOf<Group>),
    getNextPageParam: (last: Page<Group>) => (last.hasMore ? last.nextCursor : undefined),
    enabled: options.enabled ?? true,
    retry: retryUnlessRefused,
    staleTime: 60 * 1000,
  });
}

/** The flat list, for the many screens that render one grid and one "load more". */
export function flatGroups(pages: { pages: Page<Group>[] } | undefined): Group[] {
  return pages?.pages.flatMap((page) => page.items) ?? [];
}

/**
 * THE MAP'S SOURCE, AND IT IS NOT A LIST OF GROUPS ANY MORE.
 *
 * `GET /theta/groups/locations` shipped every located group — 30,000 rows on
 * one response — so the client could count them per place. `GET
 * /group-locations` is the AGGREGATE: one row per distinct `baseLocation`,
 * plus the `location: null` row that carries the "uden placering" figure the
 * old `meta.without_location` did. Drilling into a marker is
 * `GET /groups?location=<token>`, which runs the same predicate, so a facet
 * count and the list behind it cannot disagree.
 *
 * There is NO `meta.scope` on this route and the description says why: nothing
 * here counts a group the caller may not know exists.
 *
 * All pages are walked in the query function rather than by a component,
 * because a map with two thirds of its markers is not a partially-loaded map,
 * it is a wrong one. The facet is one row per PLACE, so this is bounded by the
 * Danish location dataset and not by the number of groups.
 */
export function useV1GroupLocations(parent?: string | number) {
  return useQuery({
    queryKey: ["v1", "group-locations", parent === undefined ? "" : String(parent)],
    queryFn: async () => {
      const rows: GroupLocation[] = [];
      for await (const row of paginate<GroupLocation>((cursor) =>
        thetaApi.http
          .GET("/group-locations", {
            params: {
              query: {
                ...(parent !== undefined ? { parent: String(parent) } : {}),
                ...(cursor ? { cursor } : {}),
                limit: 100,
              },
            },
          })
          .then(pageOf<GroupLocation>),
      )) {
        rows.push(row);
      }
      return rows;
    },
    retry: retryUnlessRefused,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * A group's statistics. ADMINS ONLY, and a non-admin gets a 404 rather than a
 * 403 — the same answer a group that does not exist gives (D7). The screen
 * renders that as "not found", never as an error, or the refusal becomes the
 * disclosure the port closed.
 */
export function useV1GroupStats(ref: string) {
  return useQuery({
    queryKey: ["v1", "group-stats", ref],
    queryFn: () =>
      thetaApi.http
        .GET("/groups/{ref}/stats", { params: { path: { ref } } })
        .then(dataOf<GroupStats>),
    enabled: ref.length > 0,
    retry: retryUnlessRefused,
    staleTime: 60 * 1000,
  });
}

/**
 * The gallery, as ATTACHMENTS rather than as posts-with-images.
 *
 * Each row carries real `renditions`, so the grid stops downloading full-size
 * originals to draw thumbnails — which is what `getGroupMedia` did.
 */
export function useV1GroupAttachments(ref: string, kind?: "image" | "video") {
  return useInfiniteQuery({
    queryKey: ["v1", "group-attachments", ref, kind ?? ""],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      thetaApi.http
        .GET("/groups/{ref}/attachments", {
          params: {
            path: { ref },
            query: {
              ...(kind ? { kind } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        })
        .then(pageOf<GroupAttachment>),
    getNextPageParam: (last: Page<GroupAttachment>) =>
      last.hasMore ? last.nextCursor : undefined,
    enabled: ref.length > 0,
    retry: retryUnlessRefused,
  });
}

/* -------------------------------------------------------------- mutations */

/**
 * Everything that changes a group, invalidated in one place.
 *
 * `["v1","group",ref]` is keyed by whatever the caller addressed the group by,
 * so both the slug and the id form have to be dropped rather than guessed at —
 * which is what `invalidateGroupDocuments` now does for every caller, this one
 * included. The `group` argument stays because it says at the CALL SITE that a
 * group document was touched and not only the list.
 */
function invalidateGroup(queryClient: QueryClient, group?: Pick<Group, "id" | "slug">) {
  void queryClient.invalidateQueries({ queryKey: ["v1", "groups"] });
  void queryClient.invalidateQueries({ queryKey: ["v1", "group-locations"] });
  if (group) invalidateGroupDocuments(queryClient);
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["CreateGroupBodyInput"]) =>
      thetaApi.http.POST("/groups", { body }).then(dataOf<Group>),
    onSuccess: (group) => invalidateGroup(queryClient, group),
  });
}

/**
 * Group settings, hidden tabs and the emoji policy — three legacy PUTs, one
 * PATCH (§1.7): an absent key is untouched, an explicit `null` clears, and a
 * server-owned field is `400 readOnlyField` BY NAME rather than silently
 * ignored.
 */
export function useUpdateGroup(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["UpdateGroupBodyInput"]) =>
      thetaApi.http
        .PATCH("/groups/{ref}", { params: { path: { ref } }, body })
        .then(dataOf<Group>),
    onSuccess: (group) => invalidateGroup(queryClient, group),
  });
}

export function useDeleteGroup(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      thetaApi.http
        .DELETE("/groups/{ref}", { params: { path: { ref } } })
        .then(dataOf<null>),
    onSuccess: () => invalidateGroup(queryClient),
  });
}
